import React, { useEffect, useState, useContext } from "react";
import OwnerLayout from "../../../layout/OwnerLayout";
import { useParams } from "react-router-dom";
import { API } from "../../../../api/api";
import { errorMsg } from "../../../../utils/notifications";
import {
  getPositionLogs,
  getScreenById,
  getMonthlyScreenCount,
} from "../../../../api/screen.api";
import { UserContext } from "../../../../context/UserContext";
import LogAccordionItem from "./LogAccordionItem";
function ScreenDetails() {
  let lastDate = "";
  const { id } = useParams();
  const [screenDetails, setScreenDetails] = useState(null);
  const [positionLogs, setPositionLogs] = useState([]);
  const [monthlyScreenCount, setMonthlyScreenCount] = useState([]);
  const [paidStatus, setPaidStatus] = useState({});
  const { user } = useContext(UserContext);

  // Fetch screen details
  useEffect(() => {
    getScreenById(id)
      .then((res) => {
        setScreenDetails(res?.data?.screen);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, [id]);

  // Fetch position logs only for vehicle screens
  useEffect(() => {
    if (screenDetails?.screenType === "vehicle") {
      getPositionLogs(id)
        .then((res) => {
          const sortedLogs = res?.data?.time_logs || [];
          setPositionLogs(sortedLogs);
          console.log(sortedLogs);
        })
        .catch((error) => {
          console.error("Error fetching position logs", error);
        });
    }
  }, [id, screenDetails?.screenType]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, "0")}/${String(
      date.getMonth() + 1
    ).padStart(2, "0")}/${date.getFullYear()}`;
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const checkPaymentStatus = async (months) => {
    try {
      if (!screenDetails?.vehicle?.owner && !screenDetails?.restaurant?.owner) {
        console.error("Missing ownerId or screenId", { screenDetails });
        return;
      }

      const statusMap = {};
      const ownerId =
        screenDetails?.vehicle?.owner || screenDetails?.restaurant?.owner;

      for (const entry of months) {
        const requestData = {
          ownerId: ownerId,
          screenId: screenDetails?._id,
          month: entry.month,
        };

        try {
          const response = await API.post(`/payment/status`, requestData);
          if (response.status === 200 && response.data.isPaid) {
            statusMap[entry.month] = "Paid";
          } else {
            statusMap[entry.month] = "Pending";
          }
        } catch (error) {
          if (error.response?.status === 404) {
            statusMap[entry.month] = "Pending";
          } else {
            console.error("Error fetching payment status", error);
          }
        }
      }

      setPaidStatus({ ...statusMap });
    } catch (error) {
      console.error("Error fetching payment status", error);
    }
  };

  useEffect(() => {
    getMonthlyScreenCount(id)
      .then((res) => {
        setMonthlyScreenCount(res.data.monthlyScreenCount);
      })
      .catch((error) => {
        console.error("Error fetching monthly screen count", error);
      });
  }, [id]);

  useEffect(() => {
    if (
      screenDetails &&
      (screenDetails.vehicle?.owner || screenDetails.restaurant?.owner) &&
      screenDetails._id &&
      monthlyScreenCount.length > 0
    ) {
      checkPaymentStatus(monthlyScreenCount);
    }
  }, [screenDetails, monthlyScreenCount]);

  const handleDownloadInvoice = async (month) => {
    try {
      const response = await API.get(`/payment/download`, {
        params: {
          screenId: screenDetails?._id,
          month,
        },
      });

      const { pdfLink } = response.data;
      if (pdfLink) {
        window.open(pdfLink, "_blank");
      } else {
        throw new Error("No PDF link found");
      }
    } catch (error) {
      console.error("Error downloading the invoice", error);
      errorMsg("Failed to download invoice. Please try again.");
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      Active: "bg-green-100 text-green-700",
      "In Active": "bg-orange-100 text-orange-700",
      "Under Maintenance": "bg-yellow-100 text-yellow-600",
      Retired: "bg-red-100 text-red-700",
      Approved: "bg-blue-100 text-blue-700",
      Pending: "bg-yellow-100 text-yellow-600",
      Rejected: "bg-red-100 text-red-700",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          statusClasses[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status}
      </span>
    );
  };

  const getOnlineStatusBadge = (status) => {
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          status === "offline"
            ? "bg-red-500 text-white"
            : "bg-green-500 text-white"
        }`}
      >
        {status}
      </span>
    );
  };

  if (!screenDetails) {
    return (
      <OwnerLayout title="Screen Details" ownerDetails={user}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout title="Screen Details" ownerDetails={user}>
      <div className="space-y-6 md:ml-4">
        {/* Screen Information Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-300 p-4 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  Screen Information
                </h2>
                <p className="text-sm text-gray-500">Real-time monitoring</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">Live</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Screen Details */}
            <div className="space-y-6">
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-600">
                    Model
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-800">
                  {screenDetails?.model || "N/A"}
                </span>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-600">
                    Orientation
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-800">
                  {screenDetails?.defaultOrientation || "N/A"}
                </span>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-600">
                    Status
                  </span>
                </div>
                <div>{getStatusBadge(screenDetails?.status)}</div>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-600">
                    Online Status
                  </span>
                </div>
                <div>{getOnlineStatusBadge(screenDetails?.onlineStatus)}</div>
              </div>

              {screenDetails?.imeiNumber && (
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-600">
                      IMEI
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">
                    {screenDetails.imeiNumber}
                  </span>
                </div>
              )}

              {screenDetails?.lastConnection && (
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-600">
                      Last Connection
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">
                    {new Date(screenDetails.lastConnection).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {/* Right Column - Vehicle/Restaurant Details */}
            <div className="bg-amber-100 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-yellow rounded-lg flex items-center justify-center">
                  {screenDetails?.screenType === "vehicle" ? (
                    <svg
                      className="w-4 h-4 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {screenDetails?.screenType === "vehicle"
                    ? "Vehicle Details"
                    : "Fixed Location Details"}
                </h3>
              </div>

              <div className="space-y-4">
                {screenDetails?.screenType === "vehicle" ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Brand
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {screenDetails?.vehicle?.brand || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-orange-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-600">
                          Location
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-800">
                        {screenDetails?.vehicle?.location || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Model
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {screenDetails?.vehicle?.model || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Type
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {screenDetails?.vehicle?.type || "N/A"}
                      </span>
                    </div>
                  </>
                ) : screenDetails?.screenType === "restaurant" ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Name
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {screenDetails?.restaurant?.name || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-orange-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-600">
                          Location
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-800">
                        {screenDetails?.restaurant?.location || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-red-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-600">
                          Cuisine
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-800">
                        {screenDetails?.restaurant?.cuisineType || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-purple-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-600">
                          Capacity
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-800">
                        {screenDetails?.restaurant?.seatingCapacity || "N/A"}
                      </span>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {/* Content Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Campaigns */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Active Campaigns
            </h2>
            {screenDetails?.campaigns?.length > 0 ? (
              <div className="space-y-3">
                {screenDetails.campaigns.map((campaign, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-medium text-gray-800">
                        {campaign.name}
                      </h3>
                      {getStatusBadge(campaign.status)}
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        <span className="font-medium">Period:</span>{" "}
                        {formatDateRange(campaign.startDate, campaign.endDate)}
                      </p>
                      <p>
                        <span className="font-medium">Location:</span>{" "}
                        {campaign.location}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No campaigns available
              </p>
            )}
          </div>

          {/* Entertainment Videos */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Entertainment Videos
            </h2>
            {screenDetails?.entertainmentVideos?.length > 0 ? (
              <div className="space-y-3">
                {screenDetails.entertainmentVideos.map((video, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-medium text-gray-800">
                        {video.name}
                      </h3>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                        {video.category}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        <span className="font-medium">Period:</span>{" "}
                        {formatDateRange(video.startDate, video.endDate)}
                      </p>
                      <p>
                        <span className="font-medium">Video:</span>{" "}
                        <span className="text-blue-600">
                          {video.video?.name || "N/A"}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No entertainment videos available
              </p>
            )}
          </div>
        </div>
        {/* Monthly Screen Count & Invoices */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Monthly Screen Count & Invoices
          </h2>
          {monthlyScreenCount.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Month
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Days
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {monthlyScreenCount.map((count, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                        {count.month}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800">
                        {count.days}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {paidStatus[count.month] === "Paid" ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Paid
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {paidStatus[count.month] === "Paid" && (
                          <button
                            className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-xs md:text-sm"
                            onClick={() => handleDownloadInvoice(count.month)}
                          >
                            Download
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              No monthly screen count data available
            </p>
          )}
        </div>

        {/* Position Logs  */}
        {screenDetails?.screenType && (
          <div className="bg-white rounded-lg shadow p-6 md:ml-5 md:mr-1">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {screenDetails.screenType === "vehicle"
                ? "Position Logs"
                : "Status History"}
            </h2>

            {positionLogs?.length > 0 ? (
              <div className="space-y-4">
                {(() => {
                  const groupedLogs = positionLogs.reduce((acc, log) => {
                    if (!acc[log.Date]) {
                      acc[log.Date] = [];
                    }
                    acc[log.Date].push(log);
                    return acc;
                  }, {});

                  return Object.entries(groupedLogs).map(([date, logs]) => (
                    <LogAccordionItem
                      key={date}
                      date={date}
                      logs={logs}
                      screenType={screenDetails.screenType}
                    />
                  ));
                })()}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No{" "}
                {screenDetails.screenType === "vehicle"
                  ? "position logs"
                  : "status history"}{" "}
                available
              </p>
            )}
          </div>
        )}
      </div>
    </OwnerLayout>
  );
}

export default ScreenDetails;
